import { Alert, List, Stack, Text } from "@mantine/core";

import type { EncodeFailure, GateInfo } from "../scoring";

export type GuidanceState =
  | { readonly kind: "encode-failed"; readonly reason: EncodeFailure["status"] }
  | { readonly kind: "gates-failed"; readonly failed: readonly GateInfo[] };

export interface GuidanceProps {
  readonly guidance: GuidanceState | undefined;
}

export function Guidance({ guidance }: GuidanceProps) {
  if (!guidance) return null;

  if (guidance.kind === "encode-failed") {
    return (
      <Alert color="orange" title="couldn't encode the sentence" role="status" aria-live="polite">
        <Text size="sm">{encodeFailureMessage(guidance.reason)}</Text>
      </Alert>
    );
  }

  return (
    <Alert
      color="orange"
      title="this sentence didn't pass the protocol gates"
      role="status"
      aria-live="polite"
    >
      <Stack gap="xs">
        <Text size="sm">try tweaking your sentence to address:</Text>
        <List size="sm" spacing={4}>
          {guidance.failed.map((g) => (
            <List.Item key={g.id}>{g.hint}</List.Item>
          ))}
        </List>
        <Text size="xs" c="dimmed">
          this didn&rsquo;t count against your 5 attempts — only successful scores do.
        </Text>
      </Stack>
    </Alert>
  );
}

function encodeFailureMessage(reason: EncodeFailure["status"]): string {
  switch (reason) {
    case "invalid-input":
      return "the encoder rejected the input as malformed.";
    case "word-not-in-vocab":
      return "the sentence contains a word that isn't in the Coglex vocabulary.";
    case "exceeds-token-limit":
      return "the sentence is too long for the protocol's 40-token limit.";
  }
}
